import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  addElement,
  updateModalManager,
} from "../../../reducers/configurator/configuratorSlice";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../helpers";
import { useSnackbar } from "notistack";
import { myExpariencesService } from "../../../services";
import { resetLayers } from "../../../reducers/configurator/utils";
import { modelsManagementService } from "../../../services/models.service";
import CreateIcon from "@mui/icons-material/Create";
import { rgbaToHex } from "../../../helpers";
import { useImmer } from "use-immer";
import GltfInfos from "../../forms/GltfInfos";
import AvatarCreatorV2 from "../../avatarCreator/AvatarCreatorV2";
import ThreeJsLoader from "../modelManager/threeJsLoader";
import useThreeJsLoader from "../../../zustand/stores/useThreeJsLoader";
import { useShallow } from "zustand/react/shallow";
import GetModelOrImageUrl from "../../forms/GetModelOrImageUrl";
import { useParams } from "react-router-dom";

const PREFIX = "GltfLoader";

const classes = {
  root: `${PREFIX}-root`,
  backdropStyle: `${PREFIX}-backdropStyle`,
  generatingPreviewContainer: `${PREFIX}-generatingPreviewContainer`,
  loadGltfContainer: `${PREFIX}-loadGltfContainer`,
  importFileFromDiskContainer: `${PREFIX}-importFileFromDiskContainer`,
  threeContainer: `${PREFIX}-threeContainer`,
  marginBottom: `${PREFIX}-marginBottom`,
  cancelCreatorButton: `${PREFIX}-cancelCreatorButton`,
  fakeUnloadButton: `${PREFIX}-fakeUnloadButton`,
  copyUrlContainer: `${PREFIX}-copyUrlContainer`,
  accordionColor: `${PREFIX}-accordionColor`,
  accordionDetails: `${PREFIX}-accordionDetails`,
  clipboardStyle: `${PREFIX}-clipboardStyle`,
  previewModelImageContainer: `${PREFIX}-previewModelImageContainer`,
  previewModelModifyButton: `${PREFIX}-previewModelModifyButton`,
  previewModelImage: `${PREFIX}-previewModelImage`,
  radioGroupContainer: `${PREFIX}-radioGroupContainer`,
};

const GltfLoaderContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      padding: "1rem 0",
      minWidth: "750px",
      minHeight: "250px",
      [theme.breakpoints.between("xs", "sm")]: {
        minWidth: "100%",
      },
    },
    [`& .${classes.backdropStyle} `]: {
      color: "#D3AF5F",
      zIndex: "9999",
    },
    [`& .${classes.generatingPreviewContainer}`]: {
      padding: "2rem 1rem",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      maxWidth: "350px",
      textAlign: "center",
      [theme.breakpoints.between("xs", "sm")]: {
        padding: "2rem 3rem",
      },
    },
    [`& .${classes.loadGltfContainer}`]: {
      display: "flex",
      flexDirection: "column",
      textAlign: "center",
    },

    [`& .${classes.importFileFromDiskContainer}`]: {
      margin: "1rem 0",
    },
    [`& .${classes.marginBottom}`]: {
      marginBottom: "1rem !important",
    },

    [`& .${classes.cancelCreatorButton}`]: {
      margin: "0 auto 1rem auto",
      display: "flex",
      fontSize: "0.875rem",
    },
    [`& .${classes.fakeUnloadButton}`]: {
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 9999,
      height: "64px",
      width: "75px",
      fontSize: "0.875rem",
    },
    [`& .${classes.copyUrlContainer}`]: {
      display: "flex",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
      alignItems: "center",
    },
    [`& .${classes.accordionColor}`]: {
      backgroundColor: "#363636",
      backgroundImage: "none",
    },
    [`& .${classes.accordionDetails}`]: {
      display: "flex",
      flexDirection: "column",
    },
    [`& .${classes.clipboardStyle}`]: {
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
      [theme.breakpoints.up("sm")]: {
        minWidth: "240px",
      },
      display: "flex",
      alignItems: "center",
    },

    [`& .${classes.previewModelImageContainer}`]: {
      position: "relative",
      width: "300px",
      margin: "0 auto",
    },
    [`& .${classes.previewModelModifyButton}`]: {
      position: "absolute",
      top: "0",
      right: "0",
      backgroundColor: "#303030",
    },
    [`& .${classes.previewModelImage}`]: {
      width: "300px",
      objectFit: "contain",
    },
    [`& .${classes.threeContainer}`]: {
      position: "relative",
    },
    [`& .${classes.radioGroupContainer}`]: {
      textAlign: "left",
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.up("sm")]: {
        width: "100%",
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: "1rem",
      },
    },
  };
});

const DEFAULT_COLOR = { r: 211, g: 175, b: 95, a: 0.13 };

const GltfLoader = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const setReinitialize = useThreeJsLoader((state) => state.setReinitialize);
  const setSocleColor = useThreeJsLoader((state) => state.setSocleColor);
  const {
    setStateFromProps,
    customPosition,
    animation,
    molding,
    socle,
    socleColor,
    modelUrl,
    setIsSaving,
    initialPositionVector,
    startGrid,
    endGrid,
    regeneratePreview,
    setRegeneratePreview,
    setNormalize,
    setSceneIsLoaded,
    setCanvasReady,
  } = useThreeJsLoader(
    useShallow((state) => ({
      setStateFromProps: state.setStateFromProps,
      customPosition: state.customPosition,
      animation: state.animation,
      molding: state.molding,
      socle: state.socle,
      socleColor: state.socleColor,
      modelUrl: state.modelUrl,
      setIsSaving: state.setIsSaving,
      initialPositionVector: state.initialPositionVector,
      startGrid: state.startGrid,
      endGrid: state.endGrid,
      regeneratePreview: state.regeneratePreview,
      setRegeneratePreview: state.setRegeneratePreview,
      setNormalize: state.setNormalize,
      setSceneIsLoaded: state.setSceneIsLoaded,
      setCanvasReady: state.setCanvasReady,
      setIsImage: state.setIsImage,
    }))
  );

  const [errors, setErrors] = useImmer({
    status: false,
    message: "",
    isUrlInvalid: false,
  });

  const [loaders, setLoaders] = useImmer({
    progression: 0,
    lockAPI: false,
    loadingModel: true,
    lockDisplayWebgl: false,
    generatingPreview: false,
  });

  const [fields, setFields] = useImmer({
    management: {
      previewUrl: null,
      name: "",
      iconUrl: "",
    },
    grid: {
      modelUrl: null,
      usdz: null,
      redirection: "",
      title: "",
      externalLinks: [],
      description: {
        initial: "",
        translations: [],
      },
      socleColor: DEFAULT_COLOR,
      buyable: false,
      purchasePrice: 0,
      promptAI: "",
      introductionAI: "",
      isWebXrAI: false,
    },
  });

  const [internalState, setInternalState] = useImmer({
    uploadModelFromUrl: "",
    isUrlFromAvatar: false,
    inputHexa: "",
    isAccordionOpen: false,
    layoutChoice: "normal",
    oriented: "no",
    disabled: "no",
    onlyForAdmins: "yes",
    unlimited: "no",
    isAIActive: false,
  });

  const [actionStatus, setActionStatus] = useImmer({
    isHelperOpen: false,
    isPickerOpen: false,
    isAvatarOpen: false,
    clipboard: {
      isCopied: false,
      value: "",
    },
  });
  let debouncedColor = useDebounce(fields.grid.socleColor, 500);

  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    selectedTile
      ? state.configurator.grid[parentLayout][selectedTile.row][
          selectedTile.column
        ]
      : null
  );
  const saveTrigger = useSelector(
    (state) => state.configurator.modalManager.saveTrigger
  );
  const exparienceId = useSelector(
    (state) => state.myExpariences.selectedExparience?._id
  );

  const is3dView = useSelector((state) => state.configurator?.is3dView);
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );

  useEffect(() => {
    setInternalState((draft) => {
      draft.inputHexa = rgbaToHex(
        `rgba(${debouncedColor.r}, ${debouncedColor.g}, ${debouncedColor.b}, ${debouncedColor.a})`
      );
    });
  }, [debouncedColor]);

  useEffect(() => {
    if (!loaders.lockDisplayWebgl && props.modifyElement) {
      setLoaders((draft) => {
        draft.lockDisplayWebgl = true;
      });
      loadingFinish();
    }
  }, [props.modifyElement]);

  const fieldChangeHandler = ({ category, key, value }) => {
    setFields((draft) => {
      key ? (draft[category][key] = value) : (draft[category] = value);
    });
  };

  const checkExternalLinks = () => {
    let result = true;
    if (fields.grid.externalLinks?.length === 0) return true;
    else {
      fields.grid.externalLinks?.forEach((item) => {
        if (!item.link || !item.logo || !item.title) {
          result = false;
        }
      });
    }
    return result;
  };

  const isModelLoaded = document.getElementById("modelViewer")?.loaded;

  useEffect(() => {
    if (
      props.onlyInfos ||
      (fields.grid.modelUrl &&
        checkExternalLinks() &&
        !loaders.loadingModel &&
        (((fields.grid.modelUrl?.includes(".glb") ||
          fields.grid.modelUrl?.includes(".gltf")) &&
          isModelLoaded) ||
          (!fields.grid.modelUrl?.includes(".glb") &&
            !fields.grid.modelUrl?.includes(".gltf"))))
    )
      dispatch(updateModalManager({ disableSave: false }));
  }, [
    fields.grid.modelUrl,
    fields.grid.externalLinks,
    loaders.loadingModel,
    isModelLoaded,
  ]);

  useEffect(() => {
    if (regeneratePreview) {
      modelToImg(fields.grid.modelUrl, true);
    }
  }, [regeneratePreview]);

  useEffect(() => {
    if (isModelLoaded && !fields.management.previewUrl) {
      modelToImg(fields.grid.modelUrl);
    }
  }, [isModelLoaded, fields.management.previewUrl]);

  useEffect(() => {
    if (
      saveTrigger ||
      (props.modelManagement &&
        !props.modelSaveOptions?.lock &&
        props.modelSaveOptions?.trigger)
    ) {
      const firstSplit = fields.grid.modelUrl.split("/");
      const lastSplit = firstSplit[firstSplit.length - 1].split(".")[0];
      if (
        !fields.management.previewUrl ||
        (fields.management.previewUrl &&
          !fields.management.previewUrl.includes(lastSplit))
      ) {
        setLoaders((draft) => {
          draft.generatingPreview = true;
        });
        setIsSaving();
        if (!props.modelManagement) {
          modelToImg(fields.grid.modelUrl);
        }
      } else {
        handleSaveModel();
      }
    }
  }, [
    saveTrigger,
    props.modelSaveOptions?.trigger,
    fields.management.previewUrl,
  ]);

  const setInfos = (myObj) => {
    let myY, isNormalized;
    const isImage = !(
      fields.grid.modelUrl.endsWith("glb") ||
      fields.grid.modelUrl.endsWith("gltf")
    );
    if (!myObj) {
      isNormalized = false;
      myY = isImage ? 1.6 : 0;
    } else {
      myY = myObj?.customPosition.position.y;
      // isNormalized = true;
      isNormalized =
        isImage || (!isImage && myObj.previewUrl && myObj.iconUrl)
          ? true
          : false;
    }

    setStateFromProps({
      customPosition: {
        position: [
          myObj?.customPosition.position.x -
            (myObj?.initialPositionVector?.x *
              (myObj?.customPosition.scale.x || 1) || 0) || 0,
          myY -
            (myObj?.initialPositionVector?.y *
              (myObj?.customPosition.scale.x || 1) || 0),
          myObj?.customPosition.position.z * -1 -
            (myObj?.initialPositionVector?.z *
              (myObj?.customPosition.scale.x || 1) *
              -1 || 0) || 0,
        ],
        rotation: [
          myObj?.customPosition.rotation.x || 0,
          -myObj?.customPosition.rotation.y + 180 || 0,
          myObj?.customPosition.rotation.z * -1 || 0,
        ],
        scale: myObj?.customPosition.scale.x || 1,
      },
      animation: myObj?.animation || 0,
      molding: myObj?.molding || "none",
      socle: myObj?.socle || false,
      socleColor: myObj?.socleColor || fields.grid.socleColor,
      isNormalized,
      modelUrl: fields.grid.modelUrl,
      isImage: !(
        fields.grid.modelUrl.endsWith("glb") ||
        fields.grid.modelUrl.endsWith("gltf")
      ),
    });
  };

  function loadingFinish() {
    setLoaders((draft) => {
      draft.loadingModel = false;
    });
  }

  const handleSaveCustomModel = (customModel) => {
    if (props.modelManagement) {
      // admin models management
      setLoaders((draft) => {
        draft.lockAPI = true;
      });
      if (customModel._id) {
        if (customModel.__v) delete customModel.__v;
        modelsManagementService
          .updateModel(customModel)
          .then((res) => {
            props.fetchModels();
            enqueueSnackbar(t("toasts.updateModelInManagementSuccess"), {
              variant: "success",
            });
          })
          .catch((err) => {
            if (err === 403)
              enqueueSnackbar(t("toasts.unauthorizedError"), {
                variant: "error",
              });
            else
              enqueueSnackbar(t("toasts.updateModelInManagementFailed"), {
                variant: "error",
              });
          })
          .finally(() => {
            setLoaders((draft) => {
              draft.lockAPI = true;
            });
            props.setOpenDialog(false);
            props.setCurrentModel(null);
            props.setLockSave({
              ...props.modelSaveOptions,
              trigger: false,
            });
          });
      } else {
        modelsManagementService
          .createModel(customModel)
          .then((res) => {
            props.fetchModels();
            enqueueSnackbar(t("toasts.createModelInManagementSuccess"), {
              variant: "success",
            });
          })
          .catch((err) => {
            if (err === 403)
              enqueueSnackbar(t("toasts.unauthorizedError"), {
                variant: "error",
              });
            else
              enqueueSnackbar(t("toasts.createModelInManagementFailed"), {
                variant: "error",
              });
          })
          .finally(() => {
            setLoaders((draft) => {
              draft.lockAPI = true;
            });
            props.setOpenDialog(false);
            props.setCurrentModel(null);
            props.setLockSave({
              ...props.modelSaveOptions,
              trigger: false,
            });
          });
      }
    } else {
      // configurator
      dispatch(
        addElement({ layer: "middle", data: customModel, tile: selectedTile })
      );
      dispatch(updateModalManager(resetLayers.modalManager));
      setReinitialize();
    }
  };

  const handleSaveModel = () => {
    const { modelUrl, ...gridRest } = fields.grid;
    const tmpManagement = !props.modelManagement
      ? {}
      : {
          ...fields.management,
          configuratorOptions: {
            x: startGrid - 1, // substract one for configurator algorithm, it will be the current tile + x
            y: endGrid - 1, // substract one for configurator algorithm, it will be the current tile + y
            associateCoords: startGrid > 1 && endGrid > 1,
            isEvent: internalState.layoutChoice === "event",
            isOriented: internalState.oriented === "yes",
            onFacade: false,
            disabled: internalState.disabled === "yes",
            isAdmin: internalState.onlyForAdmins === "yes",
            isTheme: false,
            unlimited: internalState.unlimited === "yes",
          },
        };
    let tmp;
    if (!props.modelManagement) {
      tmp = myTile?.middle.type ? { ...myTile.middle } : {};
      tmp.visibleRoom = myTile.visibleRoom || true;
      tmp.interaction = myTile.interaction || true;
      tmp.type = "custom";
      tmp.previewUrl = fields.management.previewUrl;
      tmp.iconUrl = fields.management.iconUrl;
    } else {
      tmp = { ...props.currentModel };
    }
    if (!props.onlyInfos) {
      tmp.customPosition = {
        position: {
          x:
            customPosition.position[0] +
            initialPositionVector[0] * customPosition.scale,
          y:
            customPosition.position[1] +
            initialPositionVector[1] * customPosition.scale,
          z:
            customPosition.position[2] * -1 +
            initialPositionVector[2] * -1 * customPosition.scale,
        },
        rotation: {
          x: customPosition.rotation[0],
          y: -customPosition.rotation[1] + 180,
          z: customPosition.rotation[2] * -1,
        },
        scale: {
          x: customPosition.scale,
          y: customPosition.scale,
          z: customPosition.scale,
        },
      };
      tmp.initialPositionVector = {
        x: initialPositionVector[0],
        y: initialPositionVector[1],
        z: initialPositionVector[2] * -1,
      };
    }
    if (!internalState.isAIActive && !gridRest.isWebXrAI) {
      gridRest.promptAI = "";
      gridRest.introductionAI = "";
    }

    tmp = {
      ...tmp,
      ...gridRest,
      socle,
      socleColor,
      animation,
      molding,
      layout: parentLayout,
      ...tmpManagement,
    };
    if (props.currentModel?._id) tmp._id = props.currentModel._id;
    const isImageOrLink = !(
      modelUrl.endsWith("gltf") || modelUrl.endsWith("glb")
    )
      ? "imageUrl"
      : "link";
    if (isImageOrLink === "imageUrl" && tmp.link) delete tmp.link;
    if (isImageOrLink === "link" && tmp.imageUrl) delete tmp.imageUrl;
    tmp[isImageOrLink] = modelUrl;
    if (isImageOrLink !== "imageUrl") delete tmp.molding;
    if (!tmp.usdz) delete tmp.usdz;
    if (props.modelManagement && tmp.hasOwnProperty("__v")) delete tmp.__v;
    handleSaveCustomModel(tmp);
  };

  useEffect(() => {
    const myObj = props.currentModel || myTile?.middle;
    if (myObj) {
      const tmpSocleColor = myObj.socleColor || DEFAULT_COLOR;
      const myUrl = myObj.molding ? myObj.imageUrl : myObj.link;
      if (myUrl) {
        setInternalState((draft) => {
          draft.isAccordionOpen = false;
        });
        setSocleColor(tmpSocleColor);
      } else {
        setInternalState((draft) => {
          draft.isAccordionOpen = true;
        });
      }
      setFields((draft) => {
        draft.grid.modelUrl = myUrl;
        draft.grid.usdz = myObj.usdz || null;
        draft.grid.description = myObj.description || {
          initial: "",
          translations: [],
        };
        draft.grid.promptAI = myObj.promptAI || "";
        draft.grid.introductionAI = myObj.introductionAI || "";
        draft.grid.isWebXrAI = myObj.isWebXrAI || false;
        draft.grid.redirection = myObj.redirection || "";
        draft.grid.title = myObj.title || "";
        draft.grid.externalLinks = myObj.externalLinks || [];
        draft.grid.buyable = myObj.buyable || false;
        draft.grid.purchasePrice = myObj.purchasePrice || 0;
        draft.grid.socleColor = tmpSocleColor;
        draft.management.previewUrl = myObj.previewUrl || null;
        draft.management.iconUrl = myObj.iconUrl || null;
        draft.management.name = myObj.name || "";
      });

      setInternalState((draft) => {
        draft.inputHexa = rgbaToHex(
          `rgba(${tmpSocleColor.r}, ${tmpSocleColor.g}, ${tmpSocleColor.b}, ${tmpSocleColor.a})`
        );
        draft.layoutChoice = myObj.configuratorOptions?.isEvent
          ? "event"
          : "normal";
        draft.isAIActive = myObj.promptAI?.length > 0;
        draft.oriented = myObj.configuratorOptions?.isOriented ? "yes" : "no";
        draft.disabled = myObj.configuratorOptions?.disabled ? "yes" : "no";
        draft.onlyForAdmins = myObj.configuratorOptions?.isAdmin ? "yes" : "no";
        draft.unlimited = myObj.configuratorOptions?.unlimited ? "yes" : "no";
      });
      setActionStatus((draft) => {
        draft.clipboard.value = myObj.link;
      });
    }
  }, [myTile?.middle, props.currentModel]);

  const modelToImg = async (url, resetLock = false) => {
    if (url.includes(".gltf") || url.includes(".glb")) {
      const tmp = document.getElementById("modelViewer");
      await tmp
        .toBlob({
          mimeType: "image/webp",
          idealAspect: true,
        })
        .then((myBlob) => {
          getS3PreviewUrlFromApi(myBlob, fields.grid.modelUrl, resetLock);
        });
    } else {
      const res = await fetch(url.endsWith(".mp4") ? url.slice(0, -4) : url);
      getS3PreviewUrlFromApi(
        new Blob([await res.blob()], { type: "image/webp" }),
        fields.grid.modelUrl,
        resetLock
      );
    }
  };

  useEffect(() => {
    if (fields.grid.modelUrl) {
      setActionStatus((draft) => {
        draft.clipboard.value = fields.grid.modelUrl;
      });

      if (
        (myTile?.middle.customPosition || props.currentModel) &&
        ((myTile?.middle.hasOwnProperty("link") &&
          myTile?.middle.link === fields.grid.modelUrl) ||
          !myTile?.middle.hasOwnProperty("link"))
      ) {
        const myObj = props.currentModel ? props.currentModel : myTile.middle;
        setInfos(myObj);
      } else {
        setInfos(null);
      }
      loadingFinish();
    }
  }, [fields.grid.modelUrl]);

  const getS3PreviewUrlFromApi = (file, blobUrl, resetLock = false) => {
    modelsManagementService
      .getModelManagementPreviewUrlFromS3(file, blobUrl, isAdmin, id)
      .then((res) => {
        setFields((draft) => {
          draft.management.previewUrl = res.url;
          draft.management.iconUrl = res.iconUrl;
        });
        setLoaders((draft) => {
          draft.generatingPreview = false;
        });
        if (resetLock) {
          setRegeneratePreview(false);
          enqueueSnackbar(t("toasts.regenerate3DModelPreview"), {
            variant: "success",
          });
        }
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.UploadImageError"), { variant: "error" });
      });
  };

  const getS3UrlFromApi = (file, fromImage = false, isUsdz = false) => {
    setLoaders((draft) => {
      draft.lockAPI = true;
    });
    setNormalize(false);
    setSceneIsLoaded(false);
    setCanvasReady(false);
    if (props.modelManagement) {
      modelsManagementService
        .getModelManagementUrlFromS3(file, isUsdz)
        .then((res) => {
          if (isUsdz) {
            enqueueSnackbar(t("toasts.uploadUsdzSuccess"), {
              variant: "success",
            });
            setFields((draft) => {
              draft.grid.usdz = res.url;
            });
          } else {
            setFields((draft) => {
              draft.grid.modelUrl = res.url;
            });
          }
          setLoaders((draft) => {
            draft.lockAPI = false;
          });
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else {
            if (fromImage)
              enqueueSnackbar(t("toasts.UploadImageError"), {
                variant: "error",
              });
            else
              enqueueSnackbar(t("toasts.UploadGltfError"), {
                variant: "error",
              });
          }
          setLoaders((draft) => {
            draft.lockAPI = false;
          });
        });
    } else {
      myExpariencesService
        .getModelUrlFromS3(exparienceId, file, isAdmin)
        .then((res) => {
          if (isUsdz) {
            enqueueSnackbar(t("toasts.uploadUsdzSuccess"), {
              variant: "success",
            });
            setFields((draft) => {
              draft.grid.usdz = res;
            });
          } else {
            setFields((draft) => {
              draft.grid.modelUrl = res;
            });
            setReinitialize();
            setInternalState((draft) => {
              draft.isAccordionOpen = false;
            });
          }
          setLoaders((draft) => {
            draft.lockAPI = false;
          });
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else {
            if (fromImage)
              enqueueSnackbar(t("toasts.UploadImageError"), {
                variant: "error",
              });
            else
              enqueueSnackbar(t("toasts.UploadGltfError"), {
                variant: "error",
              });
          }
          setLoaders((draft) => {
            draft.lockAPI = false;
          });
        });
    }
  };

  const setAvatarInformations = (data) => {
    setReinitialize();
    setInternalState((draft) => {
      draft.isAccordionOpen = false;
    });
    setFields((draft) => {
      draft.grid.modelUrl = data;
    });
    setInternalState((draft) => {
      draft.isUrlFromAvatar = true;
    });
    setActionStatus((draft) => {
      draft.isAvatarOpen = false;
    });
  };

  useEffect(() => {
    const isAnImageInFrame = [
      ".png",
      ".jpg",
      ".jpeg",
      ".psd",
      ".tiff",
      ".tga",
      ".gif",
      ".bmp",
      ".iff",
      ".pict",
    ].some((ext) => fields.grid.modelUrl?.includes(ext));
    if (
      fields.grid.modelUrl &&
      (fields.management.previewUrl || isAnImageInFrame) &&
      fields.management.name &&
      props.modelManagement
    ) {
      if (isAnImageInFrame)
        setFields((draft) => {
          draft.management.previewUrl = fields.grid.modelUrl;
        });
      props.setLockSave({ ...props.modelSaveOptions, lock: false });
    }
  }, [
    fields.grid.modelUrl,
    fields.management.previewUrl,
    fields.management.name,
  ]);

  return (
    <GltfLoaderContainer>
      <div
        className={props.onlyModel ? "" : classes.root}
        id="rootModalContainer"
      >
        <Backdrop
          className={classes.backdropStyle}
          open={loaders.generatingPreview || regeneratePreview}
        >
          <Paper elevation={12} className={classes.generatingPreviewContainer}>
            <CircularProgress size={50} color="primary" />
            <Typography variant="body1" sx={{ marginTop: "1rem" }}>
              {t("configurator.generatingModelMessage")}
            </Typography>
          </Paper>
        </Backdrop>
        {(fields.grid.modelUrl?.endsWith("gltf") ||
          fields.grid.modelUrl?.endsWith("glb")) && (
          <model-viewer
            id="modelViewer"
            src={fields.grid.modelUrl}
            style={{
              visibility: "hidden",
              position: "fixed",
              width: "650px",
              height: "650px",
              overflow: "hidden",
            }}
          />
        )}
        {actionStatus.isAvatarOpen ? (
          <div>
            <Button
              variant="contained"
              color="primary"
              className={classes.cancelCreatorButton}
              onClick={() =>
                setActionStatus((draft) => {
                  draft.isAvatarOpen = false;
                })
              }
            >
              {t("myExpariences.form.closeAvatarButton")}
            </Button>
            <div style={{ width: "100%", height: "80vh" }}>
              <AvatarCreatorV2 setAvatarInformations={setAvatarInformations} />
            </div>
          </div>
        ) : (
          <>
            <div className={classes.loadGltfContainer}>
              {props.modelManagement && (
                <Accordion
                  className={classes.accordionColor}
                  defaultExpanded
                  // disabled={!fields.grid.modelUrl}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography>
                      {t("myExpariences.form.configuratorInformations")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <Typography sx={{ marginBottom: "0.5rem" }}>
                      {t("myExpariences.form.nameForConfigurator")}
                    </Typography>
                    <TextField
                      name={`3d-model-preview-name`}
                      label={t("myExpariences.form.nameForConfigurator")}
                      value={fields.management.name}
                      id="previewName"
                      onChange={(e) =>
                        setFields((draft) => {
                          draft.management.name = e.target.value;
                        })
                      }
                      fullWidth
                      required
                      variant={"outlined"}
                    />
                    <div className={classes.radioGroupContainer}>
                      <FormControl sx={{ margin: "1rem 0 0.25rem 0" }}>
                        <FormLabel id="radio-layout-choice">
                          {t("myExpariences.form.layoutChoiceTitle")}
                        </FormLabel>
                        <RadioGroup
                          row
                          sx={{
                            justifyContent: "left",
                            alignItems: "center",
                          }}
                          aria-labelledby="radio-layout-choice"
                          name="controlled-radio-buttons-layout-choice"
                          value={internalState.layoutChoice}
                          onChange={(e) =>
                            setInternalState((draft) => {
                              draft.layoutChoice = e.target.value;
                            })
                          }
                        >
                          <FormControlLabel
                            value="normal"
                            control={<Radio />}
                            label={t("myExpariences.form.normalLayoutChoice")}
                          />
                          <FormControlLabel
                            value="event"
                            control={<Radio />}
                            label={t("myExpariences.form.eventLayoutChoice")}
                          />
                        </RadioGroup>
                      </FormControl>
                      <FormControl sx={{ margin: "0.25rem 0" }}>
                        <FormLabel id="radio-orientation-choice">
                          {t("myExpariences.form.objectOrientedTitle")}
                        </FormLabel>
                        <RadioGroup
                          row
                          sx={{
                            justifyContent: "left",
                            alignItems: "center",
                          }}
                          aria-labelledby="radio-orientation-choice"
                          name="controlled-radio-buttons-orientation-choice"
                          value={internalState.oriented}
                          onChange={(e) =>
                            setInternalState((draft) => {
                              draft.oriented = e.target.value;
                            })
                          }
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label={t("myExpariences.form.yesChoice")}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label={t("myExpariences.form.noChoice")}
                          />
                        </RadioGroup>
                      </FormControl>
                      <FormControl sx={{ margin: "0.25rem 0" }}>
                        <FormLabel id="radio-disabled-choice">
                          {t("myExpariences.form.disabledChoiceTitle")}
                        </FormLabel>
                        <RadioGroup
                          sx={{
                            justifyContent: "left",
                            alignItems: "center",
                          }}
                          row
                          aria-labelledby="radio-disabled-choice"
                          name="controlled-radio-buttons-disabled-choice"
                          value={internalState.disabled}
                          onChange={(e) =>
                            setInternalState((draft) => {
                              draft.disabled = e.target.value;
                            })
                          }
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label={t("myExpariences.form.yesChoice")}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label={t("myExpariences.form.noChoice")}
                          />
                        </RadioGroup>
                      </FormControl>
                      <FormControl sx={{ margin: "0.25rem 0" }}>
                        <FormLabel
                          id="radio-unlimited-choice"
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          {t("myExpariences.form.unlimitedChoiceTitle")}
                          <Tooltip
                            title={t(
                              "myExpariences.form.unlimitedChoiceTooltip"
                            )}
                            placement="top-end"
                            arrow
                          >
                            <HelpOutlineIcon
                              sx={{ marginLeft: "10px", cursor: "pointer" }}
                            />
                          </Tooltip>
                        </FormLabel>

                        <RadioGroup
                          sx={{
                            justifyContent: "left",
                            alignItems: "center",
                          }}
                          row
                          aria-labelledby="radio-unlimited-choice"
                          name="controlled-radio-buttons-unlimited-choice"
                          value={internalState.unlimited}
                          onChange={(e) =>
                            setInternalState((draft) => {
                              draft.unlimited = e.target.value;
                            })
                          }
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label={t("myExpariences.form.yesChoice")}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label={t("myExpariences.form.noChoice")}
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                    <div className={classes.radioGroupContainer}>
                      <FormControl sx={{ margin: "0.25rem 0" }}>
                        <FormLabel id="radio-admin-choice">
                          {t("myExpariences.form.onlyForAdminTitle")}
                        </FormLabel>
                        <RadioGroup
                          sx={{
                            justifyContent: "left",
                            alignItems: "center",
                          }}
                          row
                          aria-labelledby="radio-admin-choice"
                          name="controlled-radio-buttons-admin-choice"
                          value={internalState.onlyForAdmins}
                          onChange={(e) =>
                            setInternalState((draft) => {
                              draft.onlyForAdmins = e.target.value;
                            })
                          }
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label={t("myExpariences.form.yesChoice")}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label={t("myExpariences.form.noChoice")}
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                    {/* access only to admin */}
                    <div className={classes.importFileFromDiskContainer}>
                      {!fields.management.previewUrl ? null : (
                        <div className={classes.previewModelImageContainer}>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.psd,.tiff,.tga,.gif,.bmp,.iff,.pict"
                            name="modify 3d model preview"
                            id="preview-3d-model-modify-button"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              getS3PreviewUrlFromApi(e.target.files);
                            }}
                          />
                          <label htmlFor="preview-3d-model-modify-button">
                            <IconButton
                              color="primary"
                              aria-label="upload picture"
                              component="span"
                              className={classes.previewModelModifyButton}
                              disabled={loaders.lockAPI}
                              onChange={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              {loaders.lockAPI ? (
                                <CircularProgress size={28} color="primary" />
                              ) : (
                                <CreateIcon />
                              )}
                            </IconButton>
                          </label>
                          {fields.management.previewUrl && (
                            <>
                              <Typography>
                                {t("myExpariences.form.previewForConfigurator")}
                              </Typography>
                              <img
                                src={fields.management.previewUrl}
                                alt="preview 3D model"
                                className={classes.previewModelImage}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}
              {(props.displayAll || props.onlyInfos) && (
                <>
                  {props.modelManagement || props.displayAll ? (
                    <Accordion
                      className={classes.accordionColor}
                      disabled={
                        props.modelManagement &&
                        !fields.management.name.length > 0
                      }
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls="panel2a-content"
                        id="panel2a-header"
                      >
                        <Typography className={classes.heading}>
                          {t("configurator.modelInformations")}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        <GltfInfos
                          fieldChangeHandler={fieldChangeHandler}
                          description={fields.grid.description}
                          title={fields.grid.title}
                          buyable={fields.grid.buyable}
                          promptAI={fields.grid.promptAI}
                          introductionAI={fields.grid.introductionAI}
                          isAIActive={internalState.isAIActive}
                          isWebXrAI={fields.grid.isWebXrAI}
                          purchasePrice={fields.grid.purchasePrice}
                          redirection={fields.grid.redirection}
                          externalLinks={fields.grid.externalLinks}
                          getS3UrlFromApi={getS3UrlFromApi}
                          loaders={loaders}
                          usdz={fields.grid.usdz}
                          setInternalState={setInternalState}
                          internalState={internalState}
                          // TO REMOVE BELOW
                          modelManagement={props.modelManagement}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <GltfInfos
                      fieldChangeHandler={fieldChangeHandler}
                      description={fields.grid.description}
                      title={fields.grid.title}
                      buyable={fields.grid.buyable}
                      purchasePrice={fields.grid.purchasePrice}
                      redirection={fields.grid.redirection}
                      externalLinks={fields.grid.externalLinks}
                      getS3UrlFromApi={getS3UrlFromApi}
                      loaders={loaders}
                      usdz={fields.grid.usdz}
                      promptAI={fields.grid.promptAI}
                      introductionAI={fields.grid.introductionAI}
                      isWebXrAI={fields.grid.isWebXrAI}
                      isAIActive={internalState.isAIActive}
                      setInternalState={setInternalState}
                      internalState={internalState}
                      // TO REMOVE BELOW
                      modelManagement={props.modelManagement}
                    />
                  )}
                </>
              )}
              {!props.onlyInfos && (
                <Accordion
                  className={classes.accordionColor}
                  disabled={
                    (props.modelManagement &&
                      fields.management.name.length > 0) ||
                    !props.modelManagement
                      ? false
                      : true
                  }
                  defaultExpanded={!fields.grid.modelUrl}
                  expanded={internalState.isAccordionOpen}
                  onChange={(e, expanded) =>
                    setInternalState((draft) => {
                      draft.isAccordionOpen = expanded;
                    })
                  }
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography>
                      {t("myExpariences.form.choseModelTitle")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <GetModelOrImageUrl
                      loaders={loaders}
                      setActionStatus={setActionStatus}
                      errors={errors}
                      setErrors={setErrors}
                      setFields={setFields}
                      internalState={internalState}
                      setInternalState={setInternalState}
                      getS3UrlFromApi={getS3UrlFromApi}
                      modelUrl={fields.grid.modelUrl}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
            </div>
            {(!props.onlyInfos || props.modelManagement) &&
              fields.grid.modelUrl &&
              modelUrl && (
                <Box
                  className={classes.threeContainer}
                  sx={{ top: props.onlyModel ? "-0.6rem" : 0 }}
                >
                  <ThreeJsLoader />
                </Box>
              )}
          </>
        )}
        <Button
          disableRipple
          onClick={(e) => {
            if (!is3dView) setReinitialize();
            e.stopPropagation();
            e.preventDefault();
            dispatch(updateModalManager(resetLayers.modalManager));
            if (props.modelManagement) {
              props.setOpenDialog(false);
              props.setCurrentModel(null);
            }
          }}
          className={classes.fakeUnloadButton}
        />
      </div>
    </GltfLoaderContainer>
  );
};

export default GltfLoader;
